/**
 * JWT Token Decoder Utility
 * 
 * Decodes JWT tokens to extract claims without making network requests.
 * This is useful for checking admin status and other claims when offline
 * or when network requests might fail.
 */

export interface DecodedToken {
  claims: Record<string, any>
  exp?: number
  iat?: number
  [key: string]: any
}

/**
 * Decode a JWT token without making a network request
 * @param token - JWT token string
 * @returns Decoded token with claims, or null if decoding fails
 */
export function decodeJWT(token: string): DecodedToken | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    
    return JSON.parse(jsonPayload) as DecodedToken
  } catch (error) {
    console.warn('Failed to decode JWT token:', error)
    return null
  }
}

/**
 * Get admin status from a decoded token
 * @param claims - Token claims object
 * @returns true if user is admin, false otherwise
 */
export function isAdminFromClaims(claims: Record<string, any>): boolean {
  return claims.admin === true || claims.role === 'admin'
}

/**
 * Get admin status from Firebase user without making network request
 * Uses cached token if available, falls back to getIdTokenResult if needed
 * @param user - Firebase user object
 * @returns Promise<boolean> - true if user is admin
 */
export async function getAdminStatusFromUser(user: any): Promise<boolean> {
  if (!user) {
    return false
  }

  try {
    // Try to get cached token first to avoid network request
    const token = await user.getIdToken(false)
    
    if (token) {
      // Decode token to extract claims without network request
      const decoded = decodeJWT(token)
      if (decoded) {
        return isAdminFromClaims(decoded)
      }
    }
    
    // Fallback: use getIdTokenResult (may make network request)
    // This is only used if token decoding fails
    const idTokenResult = await user.getIdTokenResult()
    const claims = idTokenResult.claims as any
    return isAdminFromClaims(claims)
  } catch (err: any) {
    // Network errors are expected in offline scenarios
    // Silently fail and default to non-admin to avoid console spam
    if (err?.code !== 'auth/network-request-failed') {
      console.error('Error checking admin status:', err)
    }
    return false
  }
}

